/* Form Component to Add or Edit Customer details*/

import { useContext, useEffect, useState } from "react";
import {
  Button,
  Form,
  Select,
  Row,
  Col,
  Space,
  DatePicker,
  TimePicker,
} from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../components/form/form";
import { FormRow } from "../../../components/form/form-row";
import { TDropdownOptions } from "../../../types/config";
import {
  eMONTHMAPPING,
  eREPORTTYPE,
  eResultCode,
  eSCHEDULETYPE,
  eWEEKDAYS,
} from "../../../utils/enum";
import useToast from "../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import { TSchedule } from "../../../types";
import {
  AddEditReportSchedules,
  GetCustomerList,
  GetUserList,
} from "../../../utils/api.constant";
import Utils from "../../../utils";
import { UserContext } from "../../../context";
import CustomDropdown from "../../../components/custom-dropdown/custom-dropdown";
import { STAKE_HOLDERS } from "../../../utils/constants";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import useFetch from "../../../hooks/useFetch";
import dayjs from "dayjs";

type DrawerProps = {
  id: number;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
  schedule?: TSchedule;
};

export default function ScheduleReportForm(props: DrawerProps) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // const [servicePartnerOptions, setServicePartnerOptions] = useState<
  //   TDropdownOptions[]
  // >([]);
  const [userOption, setUserOption] = useState<TDropdownOptions[]>([]);
  const [monthDays, setMonthDays] = useState<TDropdownOptions[]>([
    { label: "Start of Month", value: eMONTHMAPPING.STARTOFMONTH },
    { label: "Mid of Month", value: eMONTHMAPPING.MIDOFMONTH },
    { label: "End of Month", value: eMONTHMAPPING.ENDOFMONTH },
  ]);
  const [selectedUsers, setSelectedUsers] = useState<TDropdownOptions[]>([]);
  const [scheduleType, setScheduleType] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const { post } = useFetch();
  const context: any = useContext(UserContext);
  const [currentUser, setCurrentUser] = useState<any>(context?.user);
  // UNCOMMENT THE BELOW IF NEED TO SHOW MONTH DAY FROM 1 - 31

  // const generateMonthDays = (): { label: string; value: number }[] => {
  //   const days: { label: string; value: number }[] = [];
  //   for (let i = 1; i <= 31; i++) {
  //     days.push({ label: i.toString(), value: i });
  //   }
  //   return days;
  // };

  useEffect(() => {
    getUserList();
    if (props?.schedule) {
      const {
        scheduleTime,
        reportDate,
        customerName,
        scheduleUserList,
        ...restSchedules
      } = props?.schedule as TSchedule;

      form.setFieldsValue({
        scheduleTime: dayjs(scheduleTime),
        reportDate: dayjs(reportDate),
        customerName: customerName,
        scheduleUserList,
        ...restSchedules,
      });
      setSelectedUsers(scheduleUserList as TDropdownOptions[]);
      setSelectedCustomer(customerName as any);
      setScheduleType(props?.schedule?.reportScheduleType as any);
    }
    if (context?.user?.stakeHolderName === STAKE_HOLDERS.CUSTOMER) {
      form.setFieldsValue({
        customerId: context?.user?.orgId,
        customerName: context?.user?.orgName,
      });
      setSelectedCustomer(context?.user?.orgName as any);
    }
  }, [context?.user, props?.schedule]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update validation rules dynamically based on scheduleType
  useEffect(() => {
    if (scheduleType === eSCHEDULETYPE.DAILY) {
      form.setFieldsValue({ day: undefined, reportDate: undefined });
    } else if (scheduleType === eSCHEDULETYPE.WEEKLY) {
      form.setFieldsValue({ reportDate: undefined });
    } else if (
      scheduleType === eSCHEDULETYPE.MONTHLY ||
      scheduleType === eSCHEDULETYPE.ANNUAL
    ) {
      form.setFieldsValue({ day: undefined });
    }
  }, [scheduleType, form]);

  const { onShowToast } = useToast();

  // Handle on submit event
  const onFinish = async (values: TSchedule) => {
    try {
      const { scheduleUserList, ...restValues } = values;

      const payload = {
        data: {
          id: props?.schedule?.id ?? 0,
          scheduleUserList: selectedUsers,
          ...restValues,
        },
      };
      setIsLoading(true);

      const response = await post(AddEditReportSchedules, payload);
      const { dataResponse } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        // Show a success toast message
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: description,
        });
        props?.onCloseDrawer();
        props?.onRefreshList();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getUserList = async () => {
    try {
      const payload = {
        data: {
          currentPage: 1,
          pageSize: -1,
          stakeHolderName: context?.user?.stakeHolderName,
        },
      };
      setIsLoading(true);

      const response = await post(GetUserList, payload);
      const { dataResponse, data } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        setUserOption(Utils.mapDropdownOptions(data, "id", "displayName"));
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: description,
        });
      }
    } catch (error) {
      console.log("Error while fetching user list in schedule", error);
    } finally {
      setIsLoading(false);
      console.log("User list fetched");
    }
  };

  return (
    <FormLayout>
      <Form
        autoComplete="off"
        form={form}
        name="reportForm"
        onFinish={onFinish}
        layout="vertical"
        onValuesChange={(changedValues) => {
          if (changedValues.scheduleType) {
            setScheduleType(changedValues.scheduleType);
          }
        }}
      >
        <FormHeader>
          <CloseOutlined
            style={{ cursor: "pointer", marginTop: "0.3rem", color: "white" }}
            onClick={() => {
              props.onCloseDrawer();
            }}
          />
        </FormHeader>
        <FormContent>
          <Row gutter={16} className="">
            <Space>
              <Col span={24}>
                <h2 style={{ color: "#fff", marginTop: "10px" }}>
                  Schedule Reports
                </h2>
              </Col>
            </Space>
          </Row>
          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Form.Item
                name="customerId"
                label="Customer"
                rules={[{ required: true, message: "Please choose customer" }]}
              >
                <CustomDropdown
                  isDisabled={
                    context?.user?.stakeHolderName === STAKE_HOLDERS.CUSTOMER ||
                    isLoading
                      ? true
                      : false
                  }
                  endPoint={GetCustomerList}
                  value={form.getFieldValue("customerId")}
                  selectedName={selectedCustomer || ""}
                  onChange={(selected: any) => {
                    if (selected?.servicePartners?.length) {
                      form.setFieldValue("customerId", selected.value);
                      form.setFieldValue("customerName", selected.label);
                      setSelectedCustomer(selected.label);
                      form.setFieldValue("servicePartnerId", null);
                      // setServicePartnerOptions(
                      //   Utils.mapDropdownOptions(selected?.servicePartners)
                      // );
                    } else {
                      form.setFieldValue("customerId", null);
                      form.setFieldValue("customerName", "");
                      setSelectedCustomer(null);
                      onShowToast({
                        type: ToastType.error,
                        title: <CloseOutlined />,
                        position: ToastOpen.centerTop,
                        content:
                          "Service Partner is not mapped with this customer",
                      });
                    }
                  }}
                  label=""
                  placeHolder="Select customer"
                ></CustomDropdown>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="reportType"
                label="Report Type"
                rules={[
                  { required: true, message: "Please select report type" },
                ]}
              >
                <Select
                  placeholder="Select report type"
                  options={[
                    { label: "SLA Report", value: eREPORTTYPE.SLAREPORT },
                    {
                      label: "Service Report",
                      value: eREPORTTYPE.SERVICEINSTALLATIONREPORT,
                    },
                    {
                      label: "Installation Report",
                      value: eREPORTTYPE.INSTALLATIONREPORT,
                    },
                  ]}
                />
              </Form.Item>
            </Col>

            {/* <Col span={24}>
              <Form.Item
                name="servicePartnerId"
                label="Service Partner"
                rules={[
                  {
                    required: true,
                    message: "Please choose service partner",
                  },
                ]}
              >
                <Select
                  disabled={context?.user?.stakeHolderName === "SERVICEPARTNER"}
                  placeholder="Select Service Partner"
                  options={servicePartnerOptions}
                />
              </Form.Item>
            </Col> */}

            <Col span={24}>
              <Form.Item
                name="reportScheduleType"
                label="Schedule Type"
                rules={[
                  { required: true, message: "Please select schedule type" },
                ]}
              >
                <Select
                  placeholder="Select schedule type"
                  options={[
                    { label: "Daily", value: eSCHEDULETYPE.DAILY },
                    { label: "Weekly", value: eSCHEDULETYPE.WEEKLY },
                    { label: "Monthly", value: eSCHEDULETYPE.MONTHLY },
                    { label: "Annual", value: eSCHEDULETYPE.ANNUAL },
                  ]}
                  onChange={(value: any) => setScheduleType(value)}
                />
              </Form.Item>
            </Col>

            {(scheduleType === eSCHEDULETYPE.WEEKLY ||
              scheduleType === eSCHEDULETYPE.MONTHLY) && (
              <>
                <Col span={24}>
                  <Form.Item
                    name="day"
                    label="Day"
                    rules={[{ required: true, message: "Please select day" }]}
                  >
                    <Select
                      placeholder="Select day"
                      options={
                        scheduleType === eSCHEDULETYPE.WEEKLY
                          ? [
                              { label: "Sunday", value: eWEEKDAYS.SUNDAY },
                              { label: "Monday", value: eWEEKDAYS.MONDAY },
                              { label: "Tuesday", value: eWEEKDAYS.TUESDAY },
                              {
                                label: "Wednesday",
                                value: eWEEKDAYS.WEDNESDAY,
                              },
                              {
                                label: "Thursday",
                                value: eWEEKDAYS.THURSDAY,
                              },
                              { label: "Friday", value: eWEEKDAYS.FRIDAY },
                              {
                                label: "Saturday",
                                value: eWEEKDAYS.SATURDAY,
                              },
                            ]
                          : monthDays
                      }
                    />
                  </Form.Item>
                </Col>
              </>
            )}

            {/* {scheduleType === eSCHEDULETYPE.MONTHLY && (
              <Col span={24}>
                <Form.Item
                  name="month"
                  label="Day of Month"
                  rules={[
                    { required: true, message: "Please select month day" },
                  ]}
                >
                  <Select placeholder="Select month day" options={monthDays} />
                </Form.Item>
              </Col>
            )} */}

            {scheduleType === eSCHEDULETYPE.ANNUAL && (
              <>
                <Col span={24}>
                  <Form.Item
                    name="reportDate"
                    label="Report Date"
                    rules={[
                      { required: true, message: "Please select report date" },
                    ]}
                  >
                    <DatePicker
                      format="DD-MMM-YYYY"
                      placeholder="Select report date"
                    />
                  </Form.Item>
                </Col>
              </>
            )}
            <Col span={24}>
              <Form.Item
                name="scheduleTime"
                label="Report Time"
                rules={[{ required: true, message: "Please select time" }]}
              >
                <TimePicker
                  format="HH:mm:ss"
                  placeholder="Select start time"
                  use12Hours={false}
                  style={{ width: "100%" }}
                  onChange={(time, timeString) => {
                    form.setFieldValue("scheduleTime", time);
                  }}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="scheduleUserList" label="Schedule User">
                <Select
                  mode="multiple"
                  placeholder="Select users"
                  options={userOption}
                  onChange={(value, option) => {
                    setSelectedUsers(option as any);
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="isActive"
                label="Status"
                rules={[{ required: true, message: "Please select status" }]}
              >
                <Select
                  placeholder="Select status"
                  options={[
                    { label: "Active", value: 1 },
                    { label: "Inactive", value: 0 },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
        </FormContent>

        <FormFooter className="formFooter">
          <FormRow className="formRow">
            <Button
              type="primary"
              onClick={form.submit}
              disabled={isLoading}
              className="btnDimensions"
            >
              Save
            </Button>
            <Button
              className="btnDimensions"
              type="primary"
              disabled={isLoading}
              onClick={() => {
                props.onCloseDrawer();
              }}
            >
              Cancel
            </Button>
          </FormRow>
        </FormFooter>
      </Form>
    </FormLayout>
  );
}
